import { useCallback, useEffect, useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';

import type { EditorActions } from '@atlaskit/editor-core';
import {
	updateNodeParameters,
	type UseExtensionParametersAndActionsProps,
} from '@atlassian/company-hub-common';

import type { SpotlightActions, SpotlightParameterPaths, SpotlightParameters } from '../../types';

const fetchLocalIdMacroParameters = (
	editorActions: EditorActions,
	localId: string,
): SpotlightParameters | null =>
	editorActions?.getNodeByLocalId?.(localId)?.toJSON()?.attrs?.parameters ?? null;

type ParameterUpdate<T> = { path: string; value: T };

export type UseSpotlightParametersAndActionsReturnType = {
	spotlightActions: SpotlightActions;
	spotlightParameters: SpotlightParameters | null;
};

export const useSpotlightParametersAndActions = ({
	localId,
	editorActions,
}: UseExtensionParametersAndActionsProps): UseSpotlightParametersAndActionsReturnType => {
	const [spotlightParameters, setSpotlightParameters] = useState<SpotlightParameters | null>(() =>
		fetchLocalIdMacroParameters(editorActions, localId),
	);

	useEffect(() => {
		setSpotlightParameters(fetchLocalIdMacroParameters(editorActions, localId));
	}, [editorActions, localId]);

	const updateParameter = useCallback(
		<T>(updates: ParameterUpdate<T>[]) => {
			setSpotlightParameters((prevParams) => {
				if (!prevParams) {
					return null;
				}

				const updatedSpotlightParams = cloneDeep(prevParams);
				updates.forEach(({ path, value }) => set(updatedSpotlightParams, path, value));

				updateNodeParameters(editorActions, updatedSpotlightParams);
				return updatedSpotlightParams;
			});
		},
		[editorActions],
	);

	const createUpdater =
		<T>(path: SpotlightParameterPaths) =>
		(value: T) =>
			updateParameter([{ path, value }]);

	const spotlightActions: SpotlightActions = {
		updateHeight: createUpdater<SpotlightParameters['height']>('height'),
		updateBackgroundColor: createUpdater<SpotlightParameters['backgroundColor']>('backgroundColor'),
		updateCornerStyle: createUpdater<SpotlightParameters['cornerStyle']>('cornerStyle'),
		updateLayout: createUpdater<SpotlightParameters['layout']>('layout'),
		updateContentTextTitle:
			createUpdater<SpotlightParameters['content']['text']['title']>('content.text.title'),
		updateContentTextDescription: createUpdater<
			SpotlightParameters['content']['text']['description']
		>('content.text.description'),
		updateContentTextColor:
			createUpdater<SpotlightParameters['content']['text']['textColor']>('content.text.textColor'),
		updateContentIsButtonShown: createUpdater<
			SpotlightParameters['content']['button']['isButtonShown']
		>('content.button.isButtonShown'),
		updateContentButtonText:
			createUpdater<SpotlightParameters['content']['button']['text']>('content.button.text'),
		updateContentButtonLink:
			createUpdater<SpotlightParameters['content']['button']['link']>('content.button.link'),
		updateContentButtonStyle:
			createUpdater<SpotlightParameters['content']['button']['style']>('content.button.style'),
		updateContentImage: (imageSrc, altText) => {
			updateParameter<string | boolean | undefined>([
				{ path: 'content.image.imageSrc', value: imageSrc },
				{ path: 'content.image.altText', value: altText },
			]);
		},
	};

	return { spotlightParameters, spotlightActions };
};
